'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface AdminLoginInteractiveProps {
  className?: string;
}

const AdminLoginInteractive = ({ className = '' }: AdminLoginInteractiveProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const router = useRouter();

  useEffect(() => {
    setIsHydrated(true);
    // Check if already authenticated
    const adminAuth = sessionStorage.getItem('admin_authenticated');
    if (adminAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Simple password check (in production, this should be server-side)
    // Using environment variable for admin password
    const adminPassword = process.env.NEXT_PUBLIC_ADMIN_PASSWORD || 'admin123';

    if (password === adminPassword) {
      sessionStorage.setItem('admin_authenticated', 'true');
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Invalid password. Please try again.');
      setPassword('');
    }

    setLoading(false);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_authenticated');
    setIsAuthenticated(false);
    setPassword('');
  };

  const adminFunctions = [
    {
      title: 'Admin Dashboard',
      description: 'Comprehensive control center for managing discovery call bookings and lead qualification',
      icon: 'ChartBarIcon',
      href: '/admin-dashboard',
      color: 'bg-blue-500',
    },
    {
      title: 'Proposals Management',
      description: 'Unified hub for PowerPoint templates, proposal workflows, and document generation',
      icon: 'DocumentDuplicateIcon',
      href: '/unified-proposals-management-hub',
      color: 'bg-cyan-500',
    },
    {
      title: 'Email Campaign Management',
      description: 'Create, schedule, and monitor email campaigns with template management',
      icon: 'EnvelopeIcon',
      href: '/email-campaign-management-dashboard',
      color: 'bg-green-500',
    },
    {
      title: 'Consultation Results Recording',
      description: 'Record and track consultation outcomes, recommendations, and follow-up actions',
      icon: 'DocumentTextIcon',
      href: '/consultation-results-recording-system',
      color: 'bg-purple-500',
    },
    {
      title: 'Workflow Automation System',
      description: 'Build and manage automated workflows for lead nurturing and client onboarding',
      icon: 'CogIcon',
      href: '/automated-workflow-management-system',
      color: 'bg-orange-500',
    },
    {
      title: 'Preparation Hub',
      description: 'Monitor client preparation progress and readiness assessments',
      icon: 'ClipboardDocumentCheckIcon',
      href: '/post-booking-preparation-hub',
      color: 'bg-indigo-500',
    },
  ];

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className={`min-h-screen bg-gradient-to-br from-primary/5 to-accent/5 flex items-center justify-center px-4 ${className}`}>
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                <Icon name="LockClosedIcon" size={32} variant="solid" className="text-primary" />
              </div>
              <h1 className="text-2xl font-bold text-primary font-headline mb-2">
                Admin Portal
              </h1>
              <p className="text-sm text-gray-600 font-body">
                Secure access for administrative team members
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2 font-body">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300 font-body"
                  placeholder="Enter admin password"
                  required
                  disabled={loading}
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 p-4 rounded-lg bg-red-50 border border-red-200">
                  <Icon name="ExclamationTriangleIcon" size={20} variant="solid" className="text-red-600" />
                  <p className="text-sm text-red-600 font-body">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 rounded-lg font-semibold font-cta text-white bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Authenticating...
                  </>
                ) : (
                  <>
                    Login
                    <Icon name="ArrowRightIcon" size={16} variant="solid" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <Link
                href="/"
                className="flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors duration-300 font-body"
              >
                <Icon name="ArrowLeftIcon" size={16} variant="outline" />
                Back to Homepage
              </Link>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500 font-body">
              Secure administrative access with session management and timeout protection
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard Hub (post-authentication)
  return (
    <div className={`min-h-screen bg-gradient-to-br from-primary/5 to-accent/5 ${className}`}>
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h1 className="text-4xl font-bold text-primary font-headline mb-2">
                Admin Dashboard Hub
              </h1>
              <p className="text-lg text-gray-600 font-body">
                Centralized access to all administrative functions
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold font-cta text-primary bg-white hover:bg-gray-50 border border-gray-200 transition-all duration-300"
            >
              <Icon name="ArrowRightOnRectangleIcon" size={20} variant="outline" />
              Logout
            </button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {adminFunctions.map((func, index) => (
              <Link
                key={index}
                href={func.href}
                className="group bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
              >
                <div className="p-6">
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg ${func.color} mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon name={func.icon as any} size={24} variant="solid" className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-primary font-headline mb-2 group-hover:text-accent transition-colors duration-300">
                    {func.title}
                  </h3>
                  <p className="text-sm text-gray-600 font-body leading-relaxed mb-4">
                    {func.description}
                  </p>
                  <div className="flex items-center gap-2 text-sm font-semibold text-accent group-hover:gap-3 transition-all duration-300">
                    Access Dashboard
                    <Icon name="ArrowRightIcon" size={16} variant="solid" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-12 bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <Icon name="InformationCircleIcon" size={24} variant="solid" className="text-blue-500" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-primary font-headline mb-2">
                  Security Notice
                </h3>
                <p className="text-sm text-gray-600 font-body leading-relaxed">
                  Your session is protected with automatic timeout management. For security purposes, you will be logged out after 30 minutes of inactivity. All administrative actions are monitored and logged for compliance purposes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginInteractive;